import { useState, useLayoutEffect, useCallback } from 'react'
import { useWindowSize } from 'react-use'

import { useViewportScroll, useTransform } from 'framer-motion'

export const useParallaxCalculation = ({
  ref1,
  ref2,
  disableBelow = 640,
  offsetTop = 0,
  offsetBottom = 0,
  minTranslate = 50,
}) => {
  const { width: windowWidth } = useWindowSize(
    typeof window !== 'undefined' ? window.innerWidth : 1280,
  )
  const isMobile = windowWidth < disableBelow

  const [elementTop, setElementTop] = useState(0)
  const [elementHeight, setElementHeight] = useState(0)
  const [parallaxElementHeight, setParallaxElementHeight] = useState(0)
  const [clientHeight, setClientHeight] = useState(0)
  const { scrollY } = useViewportScroll()

  const elementsDif = elementHeight - parallaxElementHeight
  const copyOffset =
    elementsDif <= 0 ? 0 : elementsDif < 100 ? minTranslate : elementsDif / 2
  const imageOffset =
    elementsDif < 0
      ? Math.abs(elementsDif) < 100
        ? minTranslate
        : elementsDif / -2
      : 0

  const initial = elementTop - clientHeight + offsetTop
  const copyFinal = elementTop + copyOffset + elementHeight - offsetBottom
  const imageFinal = elementTop + imageOffset + elementHeight - offsetBottom

  const yRef2 = useTransform(
    scrollY,
    [initial, copyFinal],
    [-copyOffset + offsetTop, copyOffset - offsetBottom],
  )

  const yRef1 = useTransform(
    scrollY,
    [initial, imageFinal],
    [-imageOffset + offsetTop, imageOffset - offsetBottom],
  )

  const onResize = useCallback(() => {
    const element = ref1.current
    const parallax = ref2.current

    if (!isMobile) {
      setElementTop(
        element.getBoundingClientRect().top + window?.scrollY ||
          window?.pageYOffset,
      )
      if (element) setElementHeight(element.getBoundingClientRect().height)
      if (parallax)
        setParallaxElementHeight(parallax.getBoundingClientRect().height)
      setClientHeight(window?.innerHeight)
    } else {
      setElementHeight(0)
      setParallaxElementHeight(0)
    }
  }, [ref1, ref2, isMobile])

  useLayoutEffect(() => {
    onResize()
  }, [onResize, windowWidth])

  return {
    yRef1,
    yRef2,
    recalculateParallax: onResize,
  }
}
